import { useState } from "react";

import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { DirectoryHealthCareProviderMapper } from "@/ContactDirectory/Api/Mapper/DirectoryHealthCareProviderMapper";
import { DirectoryPopupController } from "@/ContactDirectory/Components/DirectoryPopupController";
import { FaxDirectorySelectedEntityComponent } from "@/ContactDirectory/Components/Selection/FaxDirectorySelectedEntityComponent";
import { DirectoryHealthCareProvider } from "@/ContactDirectory/Models/DirectoryHealthCareProvider";

interface IFaxDirectoryExternalConsultantSelectionProps {
    healthCareProvider: DirectoryHealthCareProvider;
    note: string;
    onHealthCareProviderSelect: (entity: DirectoryHealthCareProvider) => void;
    onClearIntervenorSelection: () => void;
    onSelectDirectoryFaxNumber: (faxNumberId: string) => void;
    onNoteChange: (noteValue: string) => void;
    selectedFaxNumberId?: string;
}

export function FaxDirectoryExternalConsultantSelection(props: IFaxDirectoryExternalConsultantSelectionProps): JSX.Element {
    const { getText } = useTranslation();
    const [isEditProviderPopupOpen, setIsEditProviderPopupOpen] = useState<boolean>(false);
    const [note, setNote] = useState<string>(props.note);

    const getDefaultFaxNumberId = (provider: DirectoryHealthCareProvider): string => {
        const faxNumberIds = provider.getAllFaxNumbers()?.map(faxNumber => faxNumber.id);
        return faxNumberIds?.firstOrDefault(id => id === props.selectedFaxNumberId)
            ?? faxNumberIds?.firstOrDefault();
    };

    const [selectedFaxNumberId, setSelectedFaxNumberId] = useState<string>(getDefaultFaxNumberId(props.healthCareProvider));

    return (
        <>
            {isEditProviderPopupOpen && (
                <DirectoryPopupController
                    onClose={() => setIsEditProviderPopupOpen(false)}
                    onModify={entity => {
                        const healthCareProvider = entity as DirectoryHealthCareProvider;
                        const selectedFaxNumberId = getDefaultFaxNumberId(healthCareProvider);
                        if (selectedFaxNumberId) {
                            setSelectedFaxNumberId(selectedFaxNumberId);
                            props.onSelectDirectoryFaxNumber(selectedFaxNumberId);
                        }
                        props.onHealthCareProviderSelect(entity as DirectoryHealthCareProvider);
                        setIsEditProviderPopupOpen(false);
                    }}
                    model={props.healthCareProvider}
                    editModel={props.healthCareProvider}
                />
            )}
            <div className="display-flex-with-space-between">
                <div style={{ maxWidth: "450px" }}>
                    <FaxDirectorySelectedEntityComponent
                        entity={DirectoryHealthCareProviderMapper.toDirectorySelectableEntity(props.healthCareProvider)}
                        selectedNumberId={selectedFaxNumberId}
                        setSelectedNumberId={id => {
                            setSelectedFaxNumberId(id);
                            props.onSelectDirectoryFaxNumber(id);
                        }}
                        close={() => props.onClearIntervenorSelection()}
                    />
                </div>
                <a
                    className="margin-xl-left"
                    onClick={() => setIsEditProviderPopupOpen(true)}
                >
                    {getText("EditProviderLabel")}
                </a>
            </div>
            <div className="margin-sm-top">
                <label className="bold">
                    {getText("NotesLabel")}
                </label>
                <TextboxControl
                    value={note}
                    onChange={value => {
                        setNote(value);
                        props.onNoteChange(value);
                    }}
                    dataCy="externalConsultantSelection-note"
                />
            </div>
        </>
    );
}
